<!--applyPeriodSelection -->
<template>
    <div class="analises-container">
        <div class="page-header">
            <div class="page-header-left">
                <button class="btn-back" @click="$router.push('/')">
                    <i class="fas fa-arrow-left"></i>
                </button>
                <h1 class="page-title">Análises</h1>
            </div>
            <div v-if="canExportExcel" class="page-header-right">
                <b-form-select
                    v-if="isSuperAdmin"
                    v-model="exportCompanyId"
                    :options="exportEmpresas"
                    class="export-company-select"
                >
            <b-form-select-option :value="null">Selecione a empresa</b-form-select-option>
                </b-form-select>
                <button
                    class="btn-orange"
                    :disabled="exportingExcel || periodSelectLoading || (isSuperAdmin && !exportCompanyId)"
                    @click="showPeriodModal = true"
                >
                    <b-spinner v-if="exportingExcel || periodSelectLoading" small class="mr-2"></b-spinner>
                    <i v-else class="fas fa-calendar-alt mr-2"></i>
                    <span>{{ exportingExcel ? 'Exportando...' : 'Exportar por período' }}</span>
                </button>
            </div>
        </div>

        <transition name="slide">
            <div v-if="selectedItems.length > 0" class="bulk-actions-bar mb-3 p-3">
                <div class="bulk-actions-content">
                    <span class="font-weight-bold text-white">
                        {{ selectedItems.length }} iten(s) selecionado(s)
                    </span>
                    <div class="bulk-actions-buttons">
                        <button class="btn-cancel-selection" @click="clearSelection">
                            <i class="fas fa-times"></i>
                            <span>Cancelar</span>
                        </button>
                        <button v-if="isSuperAdmin" class="btn-danger-action" @click="handleBulkDelete">
                            <i class="fas fa-trash"></i>
                            <span>Excluir</span>
                        </button>
                        <button class="btn-orange-action" @click="handleBulkDownload">
                            <i class="fas fa-download"></i>
                            <span>Baixar Laudos</span>
                        </button>
                        <button class="btn-orange-action" @click="openExportModal('bulk')">
                            <i class="fas fa-file-excel"></i>
                            <span>Exportar Excel</span>
                        </button>
                        <button class="btn-orange-action" @click="handleCompareImages">
                            <i class="fas fa-images"></i>
                            <span>Comparar Imagens</span>
                        </button>
                    </div>
                </div>
            </div>
        </transition>

        <div class="box">
            <b-form-group label="" class="mb-3">
                <div class="lineflex">
                    <button class="btn-orange" @click="toggleFilters">
                        <i class="fas fa-filter mr-2"></i>Filtrar
                    </button>
                </div>
                <transition name="slide">
                    <div class="filters" v-if="showFilters">
                        <b-spinner v-if="loadingFilters" />
                        <div class="filters-row" v-else>
                            <b-form-select v-if="isSuperAdmin" v-model="filters.empresa" :options="empresas"
                                placeholder="Filtrar por Empresa" @change="onEmpresaFilterChange">
                                <b-form-select-option :value="null">Todas as Empresas</b-form-select-option>
                            </b-form-select>
                            <b-form-select v-model="filters.responsavel" :options="responsaveis"
                                placeholder="Filtrar por Responsável">
                                <b-form-select-option :value="null">Todos os Responsáveis</b-form-select-option>
                            </b-form-select>
                            <localidade-filter
                                :state.sync="filters.locationState"
                                :city.sync="filters.locationCity"
                                layout="inline"
                                :show-labels="false"
                                state-placeholder="Todos os Estados"
                                city-placeholder="Todas as Cidades"
                            />
                            <b-form-select v-model="filters.equipamento" :options="equipamentos"
                                placeholder="Filtrar por Equipamento">
                                <b-form-select-option :value="null">Selecione um Equipamento</b-form-select-option>
                            </b-form-select>
                            <div class="filters-date-range">
                                <date-range-picker
                                    :start-date.sync="filters.dataInicio"
                                    :end-date.sync="filters.dataFim"
                                    placeholder="Selecione o período"
                                />
                            </div>
                        </div>

                        <div class="filter-actions">
                            <button class="btn-orange" @click="applyFilters">
                                <i class="fas fa-check mr-2"></i>Aplicar
                            </button>
                            <button class="btn-cancel" @click="resetFilters">
                                <i class="fas fa-times mr-2"></i>Limpar filtros
                            </button>
                        </div>
                    </div>
                </transition>
            </b-form-group>

            <div v-if="loading" class="loading-progress-wrap">
                <div class="loading-progress-label">Carregando análises...</div>
                <b-progress :value="loadingProgress" :max="100" height="1.5rem" animated class="loading-progress-bar">
                    <b-progress-bar :value="loadingProgress">
                        {{ loadingProgress.toFixed(0) }}%
                    </b-progress-bar>
                </b-progress>
            </div>

            <div v-else>
                <b-card class="mb-3 full-width-card modern-card">
                    <p v-if="!totalAnalises" class="message">
                        Nenhuma análise encontrada!
                    </p>
                    <template v-else>
                        <div class="table-responsive-wrapper">
                            <b-table
                                striped
                                hover
                                :items="items"
                                :fields="tableFields"
                                class="modern-table"
                                responsive
                            >
                                <template #head(select)>
                                    <b-form-checkbox
                                        v-model="allSelected"
                                        @change="toggleSelectAll"
                                    ></b-form-checkbox>
                                </template>

                                <template #cell(select)="data">
                                    <b-form-checkbox
                                        v-model="selectedItems"
                                        :value="data.item.Id"
                                    ></b-form-checkbox>
                                </template>

                                <template #cell(actions)="data">
                                    <div class="dropdown-action-wrap">
                                        <button
                                            type="button"
                                            class="btn-actions-icon"
                                            :class="{ 'btn-actions-icon--open': openDropdownId === data.item.Id }"
                                            aria-label="Ações"
                                            @click.stop="toggleDropdown($event, data.item.Id)"
                                        >
                                            <i class="fas fa-ellipsis-v"></i>
                                        </button>
                                    </div>
                                </template>

                            </b-table>
                        </div>

                        <b-pagination
                            v-model="currentPage"
                            :total-rows="rows"
                            :per-page="pageSize"
                            align="center"
                            class="mt-3"
                        ></b-pagination>
                    </template>
                </b-card>

                <ExportOrientationModal
                    :visible="showExportModal"
                    :options="exportOptions"
                    @confirm="onExportConfirm"
                    @cancel="showExportModal = false"
            />

                <PdfModal v-if="pdfBlobUrl" :visible="showPdfModal" :pdfUrl="pdfBlobUrl"
                    @close="showPdfModal = false" />
                <ImageModal
                    v-if="showImageModal && imageBlobUrl"
                    :visible="showImageModal"
                    :imageUrl="imageBlobUrl"
                    @close="showImageModal = false"
                />
                <ImageCompareModal
                    :visible="showCompareModal"
                    :images="compareImages"
                    :loading="compareLoading"
                    :loadedCount="compareLoadedCount"
                    :total="selectedItems.length"
                    @close="closeCompareModal"
                />
            </div>

            <p v-if="error" class="error">{{ error }}</p>

            <p v-if="totalAnalises" class="results-info">
                Exibindo {{ items.length }} de {{ totalAnalises }} análises.
            </p>
        </div>

        <b-modal id="Detalhes-analise-modal" ref="detalhesModal" :title="modalTitle">
            <div v-if="selectedAnalise"></div>
        </b-modal>

        <div v-if="loadingPdf" class="pdf-loading-overlay">
            <b-spinner class="spinner" />
            <p class="text">Processando...</p>
        </div>

        <teleport to="body" v-if="openDropdownId !== null">
            <div
                class="dropdown-floating-menu"
                :style="dropdownMenuStyle"
                @click.stop
            >
                <button class="dropdown-floating-item" @click="handleDropdownAction('laudo')">
                    <i class="fas fa-file-pdf"></i>
                    <span>Laudo</span>
                </button>
                <button class="dropdown-floating-item" @click="handleDropdownAction('imagem')">
                    <i class="fas fa-image"></i>
                    <span>Imagem</span>
                </button>
                <button class="dropdown-floating-item" @click="handleDropdownAction('detalhes')">
                    <i class="fas fa-list"></i>
                    <span>Detalhes</span>
                </button>
            </div>
        </teleport>

        <b-modal
            :visible="showPeriodModal"
            title="Exportar por período"
            hide-footer
            @change="onPeriodModalChange"
        >
            <p class="period-modal-hint">Selecione os meses ou um intervalo de dias.</p>
            <date-range-picker
                v-if="showPeriodModal"
                inline
                allow-months
                :start-date.sync="periodStart"
                :end-date.sync="periodEnd"
                :disabled="periodSelectLoading"
                @apply="onPeriodApply"
            />
            <p v-if="periodSelectLoading" class="period-modal-loading">
                <b-spinner small class="mr-2"></b-spinner>
                Buscando relatórios do período...
            </p>
        </b-modal>

    </div>
</template>

<script>
/* eslint-disable */
import { request, api } from '@/services';
import { ANALYSIS_DURATION_VISIBLE_FROM, DB_ROLES, DB_ROUTES } from '../../../constants';
import { getCompanyIdFromToken } from '../../../utils';
import PdfModal from '../../modals/PdfModal.vue';
import ImageModal from '../../modals/ImageModal.vue';
import ImageCompareModal from '../../modals/ImageCompareModal.vue';
import ExportOrientationModal from '../../modals/ExportOrientationModal.vue'

export default {
    components: {
        PdfModal, ImageModal, ImageCompareModal, ExportOrientationModal
    },
    data() {
        return {
            openDropdownId: null,
            dropdownMenuStyle: {},
            loadingProgress: 0,
            loadingProgressTimer: null,
            loadingPdf: false,
            showPdfModal: false,
            pdfBlobUrl: null,
            imageBlobUrl: null,
            showImageModal: false,
            showCompareModal: false,
            compareImages: [],
            compareLoading: false,
            compareLoadedCount: 0,
            items: [],
            loading: true,
            loadingFilters: true,
            error: null,
            totalAnalises: null,
            selectedItems: [],
            allSelected: false,
            empresas: [],
            exportEmpresas: [],
            exportCompanyId: null,
            exportingExcel: false,
            responsaveis: [],
            equipamentos: [],
            selectedAnalise: null,
            modalType: null,
            modalTitle: 'Detalhes de analise',
            windowWidth: window.innerWidth,
            showExportModal: false,
            pendingExportType: null,
            showPeriodModal: false,
            periodSelectLoading: false,
            periodStart: null,
            periodEnd: null,
            periodExportIds: [],
            periodExportStart: null,
            periodExportEnd: null,
            periodMonths: null,
            periodYear: null,
            periodExportMonths: null,
            periodExportYear: null,
            exportOptions: [
                {
                    value: false,
                    title: 'Uma coluna por amostra',
                    description: 'Cada amostra será apresentada em uma coluna e as classes ficarão na primeira coluna.'
                },
                {
                    value: true,
                    title: 'Uma linha por amostra',
                    description: 'Cada amostra será apresentada em uma linha e as classes ficarão no cabeçalho.'
                }
            ],
            fields: [
                {key: 'selected', 
                    label: '',
                    thStyle: { width: '40px' }, 
                    tdClass: 'text-center' 
                },
                {
                    key: "select",
                    label: "",
                    thClass: "text-center align-middle",
                    tdClass: "text-center align-middle",
                    sortable: false
                },
                {
                    key: "Id",
                    label: "ID",
                    tdClass: "align-middle",
                    sortable: true,
                },
                {
                    key: "Nome",
                    label: "Amostra",
                    tdClass: "align-middle cell-truncate",
                    sortable: true,
                },
                {
                    key: "Data",
                    label: "Data da Análise",
                    tdClass: "align-middle",
                    sortable: true,
                },
                {
                    key: "TempoEstimado",
                    label: "Tempo Estimado",
                    tdClass: "align-middle",
                    sortable: true,
                    permission: [DB_ROLES.SUPER_ADMIN, DB_ROLES.MANAGER, DB_ROLES.COMMON],
                    hideBelow: 992
                },
                {
                    key: "Responsavel",
                    label: "Responsável",
                    tdClass: "align-middle",
                    sortable: true,
                    hideBelow: 992
                },
                {
                    key: "Equipamento",
                    label: "Equipamento",
                    tdClass: "align-middle",
                    sortable: true,
                    hideBelow: 768
                },
                {
                    key: "Cidade",
                    label: "Cidade",
                    tdClass: "align-middle",
                    sortable: true,
                    hideBelow: 1200
                },
                {
                    key: "Estado",
                    label: "Estado",
                    tdClass: "align-middle",
                    sortable: true,
                    hideBelow: 1200
                },
                {
                    key: "Empresa",
                    label: "Empresa",
                    tdClass: "align-middle",
                    sortable: true,
                    permission: [DB_ROLES.SUPER_ADMIN],
                    hideBelow: 1400
                },
                {
                    key: "Conclusao",
                    label: "Conclusão",
                    tdClass: value => `align-middle ${this.getStatusClass(value)}`,
                    sortable: true,
                },
                {
                    key: "PctAvariados",
                    label: "% Avariados",
                    tdClass: "align-middle",
                    sortable: true,
                    hideBelow: 992
                },
                {
                    key: "PesoPrato",
                    label: "Peso da Amostra (g)",
                    tdClass: "align-middle",
                    sortable: true,
                    hideBelow: 1200
                },
                {
                    key: "actions",
                    label: "Ações",
                    tdClass: "align-middle actionsWrapper",
                    thClass: "text-center",
                    stickyColumn: true
                }
            ],
            filters: {
                empresa: null,
                responsavel: null,
                locationState: null,
                locationCity: null,
                equipamento: null,
                dataInicio: null,
                dataFim: null
            },
            showFilters: false,
            pageSize: 7,
            currentPage: 1,
        };
    },
    computed: {
        isSuperAdmin() {
            return this.$store.getters.isSuperAdmin;
        },
        isManager() {
            return this.$store.getters.isManager;
        },
        isOperator() {
            return this.$store.getters.isOperator;
        },
        isCommonUser() {
            return this.$store.getters.isReadOnly;
        },
        canExportExcel() {
            return this.isSuperAdmin || this.isManager || this.isCommonUser;
        },
        rows() {
            return this.totalAnalises;
        },
        tableFields() {
            return this.fields.filter(field => {
                if (field.permission && !field.permission.includes(this.$store.getters.userRole)) {
                    return false;
                }
                if (field.hideBelow && this.windowWidth < field.hideBelow) {
                    return false;
                }
                return true;
            });
        }
    },
    watch: {
        responsaveis(newOptions) {
            const employeeName = this.$route.query.employee;
            if (newOptions && employeeName) {
                this.filters.responsavel = employeeName;
                this.applyFilters();
            }
        },
        currentPage(newPage) {
            if (newPage) {
                if (this.isOperator && this._allOperatorItems) {
                    const start = (newPage - 1) * this.pageSize;
                    this.items = this._allOperatorItems.slice(start, start + this.pageSize);
                } else {
                    this.fetchAnalises();
                }
            }
        },
       selectedItems(newVal) {
    // Verifica se todos os itens da página atual estão dentro do array selecionado
    const allCurrentPageSelected = this.items.every(item => newVal.includes(item.Id));
    
    if (this.items.length > 0 && allCurrentPageSelected) {
        this.allSelected = true;
    } else {
        this.allSelected = false;
    }
},
        items() {
            this.$nextTick(() => { this.checkTableScroll(); });
        },
        loading(newVal) {
            if (newVal) {
                this.startLoadingProgress();
            } else {
                this.stopLoadingProgress();
                this.$nextTick(() => { this.checkTableScroll(); });
            }
        }
    },
    methods: {
        startLoadingProgress() {
            this.loadingProgress = 0;
            clearInterval(this.loadingProgressTimer);
            this.loadingProgressTimer = setInterval(() => {
                if (this.loadingProgress < 85) {
                    const increment = (85 - this.loadingProgress) * 0.08;
                    this.loadingProgress = Math.min(85, this.loadingProgress + increment);
                }
            }, 80);
        },
        stopLoadingProgress() {
            clearInterval(this.loadingProgressTimer);
            this.loadingProgress = 100;
        },
        toggleDropdown(event, id) {
            if (this.openDropdownId === id) {
                this.openDropdownId = null;
                return;
            }

            const btn = event.currentTarget;
            const rect = btn.getBoundingClientRect();
            const menuWidth = 160;
            const menuHeight = 132;
            const viewportH = window.innerHeight;

            const openUpward = rect.bottom + menuHeight > viewportH;
            const top = openUpward ? rect.top - menuHeight - 4 : rect.bottom + 4;

            let left = rect.right - menuWidth;
            if (left < 8) left = rect.left;

            this.dropdownMenuStyle = {
                top: `${top}px`,
                left: `${left}px`,
                width: `${menuWidth}px`,
            };

            this.openDropdownId = id;
        },
        closeDropdown() {
            this.openDropdownId = null;
        },
       handleDropdownAction(action) {
            const id = this.openDropdownId;
            this.openDropdownId = null;

            if (action === 'laudo') {
                this.exportReport(id);
            } else if (action === 'imagem') {
                this.exportImage(id);
            } else if (action === 'detalhes') {
                const item = this.items.find(i => i.Id === id);
                
                
                sessionStorage.setItem('paginaSalva', this.currentPage); 
                
                if (item) this.goToClassificar(item);
            }
        },
        onPeriodModalChange(isVisible) {
            if (!isVisible) this.showPeriodModal = false;
        },
        formatPercent(value) {
            if (value == null || value === '') return '---';
            const num = Number(value);
            if (!Number.isFinite(num)) return '---';
            return `${num.toFixed(2)}%`;
        },
        formatWeight(value) {
            if (value == null || value === '') return '---';
            const num = Number(value);
            if (!Number.isFinite(num)) return '---';
            return num.toFixed(2);
        },
        mapAnaliseRow(analise) {
            const totalDamagedPercentage =
                analise.totalDamagedPercentage
                ?? analise.TotalDamagedPercentage
                ?? analise.total_damaged_percentage;
            const grossSampleWeight =
                analise.grossSampleWeight
                ?? analise.GrossSampleWeight
                ?? analise.gross_sample_weight;

            const startDate = analise.startDate ?? analise.StartDate;
            const endDate = analise.endDate ?? analise.EndDate;

            return {
                Id: analise.id ?? analise.Id,
                Nome: analise.sampleId ?? analise.SampleId,
                Data: this.formatarData(startDate),
                DataRaw: startDate,
                TempoEstimado: this.formatarTempoEstimado(startDate, endDate),
                Responsavel: this.getResponsavelName(analise) || analise.employeeId || analise.EmployeeId,
                Equipamento: analise.equipmentIdentification || analise.EquipmentIdentification || '---',
                Cidade: analise.locationCity || analise.LocationCity || analise.locationId,
                Estado: analise.locationState || analise.LocationState || analise.locationId,
                Empresa: analise.companyName || analise.CompanyName || analise.companyId,
                Conclusao: analise.non_standard ? 'Fora do padrão' : 'Padrão básico',
                PctAvariados: this.formatPercent(totalDamagedPercentage),
                PesoPrato: this.formatWeight(grossSampleWeight)
            };
        },
       toggleSelectAll(checked) {
    if (checked) {
        // Adiciona apenas os IDs da página atual que ainda não estão no array
        this.items.forEach(item => {
            if (!this.selectedItems.includes(item.Id)) {
                this.selectedItems.push(item.Id);
            }
        });
    } else {
        // Remove apenas os IDs que estão visíveis na página atual
        this.items.forEach(item => {
            const index = this.selectedItems.indexOf(item.Id);
            if (index > -1) {
                this.selectedItems.splice(index, 1);
            }
        });
    }
},
        handleBulkDelete() {
            if (!confirm(`Tem certeza que deseja excluir ${this.selectedItems.length} análises? Esta ação não pode ser desfeita.`)) {
                return;
            }
            this.loading = true;
            request.post(
                DB_ROUTES.MULTIPLE_ANALYSES_DELETE,
                this.selectedItems,
                () => {
                    this.$toast && this.$toast.success('Análises excluídas com sucesso!');
                    this.selectedItems = [];
                    this.allSelected = false;
                    this.fetchAnalises();
                },
                error => {
                    console.error(error);
                    this.loading = false;
                    this.$toast && this.$toast.error('Erro ao excluir análises.');
                }
            );
        },
        handleBulkDownload() {
            this.loadingPdf = true;
            request.post(
                DB_ROUTES.EXPORT_LAUDOS_PDF_MULTIPLE,
                this.selectedItems,
                response => {
                    const blob = new Blob([response.data], { type: 'application/zip' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `Laudos_Exportados_${new Date().getTime()}.zip`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                    this.loadingPdf = false;
                    this.selectedItems = [];
                    this.allSelected = false;
                    this.$toast && this.$toast.success('Laudos baixados com sucesso!');
                },
                error => {
                    this.loadingPdf = false;
                    console.error('Erro ao baixar laudos múltiplos:', error);
                    this.$toast && this.$toast.error('Falha ao baixar os laudos.');
                },
                { responseType: 'arraybuffer' }
            );
        },
handleBulkExportExcel(pivoted, idsOverride) {
    const ids = idsOverride || this.selectedItems;
    if (!ids.length) return;

    this.exportingExcel = true;

    api.post(
        DB_ROUTES.EXPORT_LAUDOS_EXCEL_MULTIPLE,
        ids,
        { responseType: 'arraybuffer', params: { pivoted } }
    ).then(response => {
        const disposition = response.headers?.['content-disposition'] || '';
        const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
        const periodFileName = idsOverride
            ? this.formatPeriodFileName(this.periodExportStart, this.periodExportEnd, this.periodExportMonths, this.periodExportYear)
            : '';
        const fileName = periodFileName
            || (match ? match[1].replace(/['"]/g, '') : `Laudos_selecionados.xlsx`);

        const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.exportingExcel = false;
        if (!idsOverride) {
            this.selectedItems = [];
        } else {
            this.periodExportIds = [];
            this.periodExportStart = null;
            this.periodExportEnd = null;
            this.periodExportMonths = null;
            this.periodExportYear = null;
        }
        this.$toast && this.$toast.success('Excel exportado com sucesso!');
    }).catch(error => {
        this.exportingExcel = false;
        console.error('Erro ao exportar laudos Excel:', error);
        const status = error.response?.status;
        if (status === 404) {
            this.$toast && this.$toast.error('Nenhum laudo encontrado para esta empresa.');
        } else if (status === 401 || status === 403) {
            this.$toast && this.$toast.error('Sem permissão para exportar laudos desta empresa.');
        } else {
            this.$toast && this.$toast.error('Falha ao exportar Excel.');
        }
    });
},
            openExportModal(type) {
            this.pendingExportType = type;
            this.showExportModal = true;
        },
        onExportConfirm({ pivoted }) {
            this.showExportModal = false;
    if (this.pendingExportType === 'company') {
        this.exportLaudosExcel(pivoted);
    } else if (this.pendingExportType === 'bulk') {
        this.handleBulkExportExcel(pivoted);
    } else if (this.pendingExportType === 'period') {
        this.handleBulkExportExcel(pivoted, this.periodExportIds);
    }
        },

selectReportsByPeriod() {
    if (!this.periodStart || !this.periodEnd) {
        this.$toast && this.$toast.error('Informe a data inicial e a data final do período.');
        return;
    }

    const start = new Date(this.periodStart);
    start.setUTCHours(0, 0, 0, 0);

    const end = new Date(this.periodEnd);
    end.setUTCHours(23, 59, 59, 999); 

    if (start > end) {
        this.$toast && this.$toast.error('A data inicial não pode ser depois da data final.');
        return;
    }

    this.periodSelectLoading = true;

    if (this.isOperator && this._allOperatorItems) {
        const matched = this._allOperatorItems.filter(item => {
            if (!item.DataRaw) return false;
            const d = new Date(item.DataRaw);
            return d >= start && d <= end && this.matchesSelectedMonths(item.DataRaw);
        });
        this.applyPeriodSelection(matched.map(item => item.Id));
        this.periodSelectLoading = false;
        return;
    }

    const params = new URLSearchParams();
    params.append('startDate', start.toISOString());
    params.append('endDate', end.toISOString());
    const selectedCompany = this.isSuperAdmin
        ? this.exportEmpresas.find(empresa => empresa.value === this.exportCompanyId)
        : null;
    const companyName = selectedCompany?.text || this.filters.empresa;
    if (companyName) params.append('companyName', companyName);

    request.get(
        DB_ROUTES.GET_ANALYSES_BY_FILTERS(params.toString()),
        response => {
            const data = Array.isArray(response.data) ? response.data : [];
            const ids = data
                .filter(item => this.matchesSelectedMonths(item.startDate ?? item.StartDate))
                .map(a => a.id ?? a.Id);
            this.applyPeriodSelection(ids);
            this.periodSelectLoading = false;
        },
        error => {
            console.error('Erro ao selecionar relatórios por período:', error);
            this.$toast && this.$toast.error('Erro ao buscar relatórios do período.');
            this.periodSelectLoading = false;
        }
    );
},

clearSelection() {
    this.selectedItems = [];
    this.allSelected = false;
},

applyPeriodSelection(ids) {
    if (!ids.length) {
        this.$toast && this.$toast.error('Nenhum relatório encontrado nesse período.');
        return;
    }

    this.periodExportIds = ids;
    this.periodExportStart = this.periodStart;
    this.periodExportEnd = this.periodEnd;
    this.periodExportMonths = this.periodMonths ? this.periodMonths.slice() : null;
    this.periodExportYear = this.periodYear;
    this.periodMonths = null;
    this.periodYear = null;
    this.periodStart = null;
    this.periodEnd = null;
    this.showPeriodModal = false;
    this.openExportModal('period');
},

matchesSelectedMonths(rawDate) {
    if (!this.periodMonths || !this.periodMonths.length) return true;
    const match = String(rawDate || '').match(/(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return false;
    if (this.periodYear && Number(match[1]) !== Number(this.periodYear)) return false;
    return this.periodMonths.includes(Number(match[2]));
},

formatPeriodFileName(start, end, months, year) {
    if (Array.isArray(months) && months.length && year) {
        return this.formatMonthGroupsFileName(months, year);
    }

    const formatPart = (iso) => {
        const match = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (!match) return '';
        return `${match[3]}.${match[2]}`;
    };

    const startLabel = formatPart(start);
    const endLabel = formatPart(end);
    if (!startLabel) return '';
    if (!endLabel || startLabel === endLabel) return `${startLabel}.xlsx`;
    return `${startLabel}-${endLabel}.xlsx`;
},

formatMonthGroupsFileName(months, year) {
    const sorted = [...months].sort((a, b) => a - b);
    const groups = [];
    sorted.forEach(month => {
        const current = groups[groups.length - 1];
        if (current && month === current[current.length - 1] + 1) current.push(month);
        else groups.push([month]);
    });

    const parts = groups.map(group => {
        const startMonth = String(group[0]).padStart(2, '0');
        const endMonth = group[group.length - 1];
        const endMonthLabel = String(endMonth).padStart(2, '0');
        const lastDay = String(new Date(year, endMonth, 0).getDate()).padStart(2, '0');
        const startLabel = `01.${startMonth}`;
        const endLabel = `${lastDay}.${endMonthLabel}`;
        return startLabel === endLabel ? startLabel : `${startLabel}-${endLabel}`;
    });

    return `${parts.join('_')}.xlsx`;
},

onPeriodApply({ startDate, endDate, months, year }) {
    this.periodStart = startDate;
    this.periodEnd = endDate;
    this.periodMonths = Array.isArray(months) && months.length ? months.slice() : null;
    this.periodYear = year || null;
    this.selectReportsByPeriod();
},

        handleCompareImages() {
            this.compareImages = [];
            this.compareLoadedCount = 0;
            this.compareLoading = true;
            this.showCompareModal = true;

            const sampleNameMap = {};
            this.items.forEach(item => {
                sampleNameMap[item.Id] = item.Nome || item.Id;
            });

            const promises = this.selectedItems.map(id =>
                api.get(DB_ROUTES.GET_AWS_PATH(id, 'image'), { responseType: 'blob' })
                    .then(response => {
                        this.compareLoadedCount++;
                        return {
                            analysisId: id,
                            sampleName: sampleNameMap[id] || id,
                            imageUrl: URL.createObjectURL(response.data)
                        };
                    })
                    .catch(() => {
                        this.compareLoadedCount++;
                        return null;
                    })
            );

            Promise.all(promises).then(results => {
                this.compareImages = results.filter(Boolean);
                this.compareLoading = false;
            });
        },
        closeCompareModal() {
            this.showCompareModal = false;
            this.compareImages.forEach(img => {
                if (img.imageUrl) URL.revokeObjectURL(img.imageUrl);
            });
            this.compareImages = [];
        },
        toggleFilters() {
            this.showFilters = !this.showFilters;
            if (this.showFilters) {
                this.fetchFiltersData();
            }
        },
        getResponsavelName(analise) {
            return analise.employeeName || analise.EmployeeName || null;
        },
        fetchFiltersData() {
            if (this.isSuperAdmin) this.fetchEmpresas();
            this.fetchEquipamentos();
            this.fetchResponsaveis();
        },
        fetchAnalises() {
            this.loading = true;
            if (this.isOperator) {
                request.get(
                    DB_ROUTES.GET_ALL_ANALYSES_PAGED(this.currentPage, this.pageSize),
                    response => {
                        const data = response.data;
                        const list = Array.isArray(data) ? data : (data?.items || []);
                        if (list.length > 0 || data?.totalCount === 0) {
                            this.items = list.map(analise => this.mapAnaliseRow(analise));
                            this.totalAnalises = data?.totalCount ?? this.items.length;
                            this._allOperatorItems = this.items;
                        } else {
                            this.items = [];
                            this.totalAnalises = null;
                        }
                        this.loading = false;
                    },
                    error => {
                        console.log(error);
                        this.error = "Erro ao carregar dados das análises";
                        this.loading = false;
                    }
                );
            } else {
                request.get(
                    DB_ROUTES.GET_ALL_ANALYSES_PAGED(this.currentPage, this.pageSize),
                    response => {
                        const data = response.data;
                        if (data) {
                            console.log(data);
                            this.items = data.items.map(analise => this.mapAnaliseRow(analise));
                            this.totalAnalises = data.totalCount;
                        } else {
                            if (response.status !== 204) {
                                this.error = "Erro ao carregar dados das análises";
                            }
                            this.totalAnalises = null;
                        }
                        this.loading = false;
                    },
                    error => {
                        console.log(error);
                        this.error = "Erro ao carregar dados das análises";
                        this.loading = false;
                    }
                );
            }
        },
        getReportStatus() {
            const status = ['Fora do padrão', 'Padrão básico'];
            return status[Math.floor(Math.random() * status.length)];
        },
        getStatusClass(value) {
            if (value === 'Fora do padrão') return 'not-approved';
            return '';
        },
        fetchEmpresas() {
            if (this.empresas.length > 0) return;
            this.loadingFilters = true;
            request.get(
                DB_ROUTES.GET_ALL_COMPANIES,
                response => {
                    this.empresas = response.data?.map(empresa => ({
                        value: empresa.name,
                        text: empresa.name
                    })) || [];
                    this.loadingFilters = false;
                },
                error => {
                    console.log(error);
                    this.error = "Erro ao carregar as empresas.";
                    this.loadingFilters = false;
                }
            );
        },
        fetchEquipamentos() {
            if (this.equipamentos.length > 0) return;
            this.loadingFilters = true;
            request.get(
                DB_ROUTES.GET_ALL_EQUIPMENTS,
                response => {
                    this.equipamentos = response.data?.map(equipamento => ({
                        value: equipamento.id,
                        text: equipamento.equipmentIdentification
                    })) || [];
                    this.loadingFilters = false;
                },
                error => {
                    console.log(error);
                    this.loadingFilters = false;
                }
            );
        },
        onEmpresaFilterChange() {
            this.filters.responsavel = null;
            this.fetchResponsaveis();
        },
        fetchResponsaveis() {
            this.loadingFilters = true;
            request.get(
                DB_ROUTES.GET_ALL_ANALYSES,
                response => {
                    const payload = response.data;
                    let data = Array.isArray(payload) ? payload : (payload?.items || []);

                    if (this.isSuperAdmin && this.filters.empresa) {
                        data = data.filter(analise => {
                            const companyName = analise.companyName || analise.CompanyName;
                            return companyName === this.filters.empresa;
                        });
                    }

                    const names = [...new Set(
                        data
                            .map(analise => this.getResponsavelName(analise))
                            .filter(Boolean)
                    )].sort((a, b) => a.localeCompare(b, 'pt-BR'));

                    this.responsaveis = names.map(name => ({
                        value: name,
                        text: name,
                    }));
                    this.loadingFilters = false;
                },
                error => {
                    console.log(error);
                    this.error = "Erro ao carregar responsáveis.";
                    this.responsaveis = [];
                    this.loadingFilters = false;
                }
            );
        },
        applyFilters() {
            if (this.isOperator) {
                this.loading = true;
                const params = new URLSearchParams();
                if (this.filters.dataInicio) {
                    params.append('startDate', new Date(this.filters.dataInicio).toISOString());
                }
                if (this.filters.dataFim) {
                    params.append('endDate', new Date(this.filters.dataFim).toISOString());
                }
                if (this.filters.locationState) params.append('state', this.filters.locationState);
                if (this.filters.locationCity)  params.append('city', this.filters.locationCity);
                if (this.filters.responsavel) params.append('employee', this.filters.responsavel);
                if (this.filters.equipamento) {
                    const selectedEquip = this.equipamentos.find(e => e.value === this.filters.equipamento);
                    if (selectedEquip) params.append('equipmentIdentifier', selectedEquip.text);
                }

                request.get(
                    DB_ROUTES.GET_ANALYSES_BY_FILTERS(params.toString()),
                    response => {
                        if (Array.isArray(response.data) && response.data.length > 0) {
                            this.items = response.data.map(analise => this.mapAnaliseRow(analise));
                            this.totalAnalises = this.items.length;
                            this.currentPage = 1;
                            this.pageSize = this.items.length;
                            this._allOperatorItems = this.items;
                        } else {
                            this.items = [];
                            this.totalAnalises = null;
                        }
                        this.loading = false;
                    },
                    error => {
                        console.log(error);
                        this.error = "Erro ao obter análises filtradas";
                        this.loading = false;
                        this.totalAnalises = null;
                    }
                );
                return;
            }

            const params = new URLSearchParams();
            if (this.filters.dataInicio) {
                params.append('startDate', new Date(this.filters.dataInicio).toISOString());
            }
            if (this.filters.dataFim) {
                params.append('endDate', new Date(this.filters.dataFim).toISOString());
            }
            if (this.filters.locationState) params.append('state', this.filters.locationState);
            if (this.filters.locationCity)  params.append('city', this.filters.locationCity);
            if (this.filters.responsavel) params.append('employee', this.filters.responsavel);
            if (this.filters.equipamento) {
                const selectedEquip = this.equipamentos.find(e => e.value === this.filters.equipamento);
                if (selectedEquip) params.append('equipmentIdentifier', selectedEquip.text);
            }
            if (this.filters.empresa) params.append('companyName', this.filters.empresa);

            this.loading = true;
            request.get(
                DB_ROUTES.GET_ANALYSES_BY_FILTERS(params.toString()),
                response => {
                    if (Array.isArray(response.data) && response.data.length > 0) {
                        this.items = response.data.map(analise => this.mapAnaliseRow(analise));
                        this.totalAnalises = this.items.length;
                        this.currentPage = 1;
                        this.pageSize = this.items.length;
                    } else {
                        this.items = [];
                        this.totalAnalises = null;
                    }
                    this.loading = false;
                },
                error => {
                    console.log(error);
                    this.error = "Erro ao obter análises filtradas";
                    this.loading = false;
                    this.totalAnalises = null;
                }
            );
        },
        formatarData(data) {
            const [datePart, timePart] = data.split('T');
            const [year, month, day] = datePart.split('-');
            const [hour, minute, secondWithMS] = timePart.split(':');
            const second = secondWithMS.split('.')[0];
            return ` ${day}/${month}/${year} \n ${hour}:${minute}:${second}`;
        },
        formatarTempoEstimado(startDate, endDate) {
            if (!startDate || !endDate) return '---';

            const start = new Date(startDate);
            const end = new Date(endDate);
            if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '---';
            if (start < ANALYSIS_DURATION_VISIBLE_FROM) return '---';

            const diffMs = end.getTime() - start.getTime();
            if (diffMs < 0) return '---';

            const totalSeconds = Math.floor(diffMs / 1000);
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;

            if (hours > 0) {
                return `${hours}h ${String(minutes).padStart(2, '0')}min ${String(seconds).padStart(2, '0')}s`;
            }
            if (minutes > 0) {
                return `${minutes}min ${String(seconds).padStart(2, '0')}s`;
            }
            return `${seconds}s`;
        },
        exportReport(analysisId) {
            this.loadingPdf = true;
            request.get(
                DB_ROUTES.GET_LAUDO_PDF(analysisId),
                response => {
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    const url = URL.createObjectURL(blob);
                    this.pdfBlobUrl = url;
                    this.showPdfModal = true;
                    this.loadingPdf = false;
                },
                error => {
                    this.loadingPdf = false;
                    console.error('Erro ao baixar PDF:', error);
                    const status = error.response?.status;
                    if (status === 404) {
                        this.$toast && this.$toast.error('Laudo não encontrado para esta análise no S3.');
                    } else if (status === 502 || status === 503) {
                        this.$toast && this.$toast.error('Erro de comunicação com o servidor de arquivos. Tente novamente.');
                    } else if (status === 401 || status === 403) {
                        this.$toast && this.$toast.error('Sem permissão para acessar este laudo.');
                    } else {
                        this.$toast && this.$toast.error(`Falha ao exportar relatório. (Status: ${status || 'desconhecido'})`);
                    }
                },
                { responseType: 'arraybuffer' }
            );
        },
        exportImage(analysisId) {
            this.loadingPdf = true;
            request.get(
                DB_ROUTES.GET_AWS_PATH(analysisId, 'image'),
                (response) => {
                    const blob = new Blob([response.data], { type: 'image/jpeg' });
                    const url = URL.createObjectURL(blob);
                    this.imageBlobUrl = url;
                    this.showImageModal = true;
                    this.loadingPdf = false;
                },
                (error) => {
                    this.loadingPdf = false;
                    console.error('Erro ao baixar imagem:', error);
                    const status = error.response?.status;
                    if (status === 404) {
                        this.$toast && this.$toast.error('Imagem não encontrada para esta análise no S3.');
                    } else if (status === 502 || status === 503) {
                        this.$toast && this.$toast.error('Erro de comunicação com o servidor de arquivos. Tente novamente.');
                    } else if (status === 401 || status === 403) {
                        this.$toast && this.$toast.error('Sem permissão para acessar esta imagem.');
                    } else {
                        this.$toast && this.$toast.error(`Erro ao baixar imagem. (Status: ${status || 'desconhecido'})`);
                    }
                },
                { responseType: 'blob' }
            );
        },
        fetchExportEmpresas() {
            if (this.exportEmpresas.length > 0) return;
            request.get(
                DB_ROUTES.GET_ALL_COMPANIES,
                response => {
                    this.exportEmpresas = (response.data || []).map(empresa => ({
                        value: empresa.id,
                        text: empresa.name
                    }));
                },
                error => {
                    console.error('Erro ao carregar empresas para exportação:', error);
                    this.$toast && this.$toast.error('Erro ao carregar lista de empresas.');
                }
            );
        },
        resolveExportCompanyId() {
            if (this.isSuperAdmin) {
                return this.exportCompanyId ? Number(this.exportCompanyId) : null;
            }
            return getCompanyIdFromToken();
        },
exportLaudosExcel(pivoted) {
    const companyId = this.resolveExportCompanyId();
    if (!companyId) {
        this.$toast && this.$toast.error(
            this.isSuperAdmin
                ? 'Selecione uma empresa para exportar.'
                : 'Empresa não encontrada no token do usuário.'
        );
        return;
    }

    this.exportingExcel = true;
    request.get(
        DB_ROUTES.EXPORT_LAUDOS_EXCEL(companyId),
        response => {
            const disposition = response.headers?.['content-disposition'] || '';
            const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
            const fileName = match ? match[1].replace(/['"]/g, '') : `Laudos-Company-${companyId}.xlsx`;

            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            this.exportingExcel = false;
            this.$toast && this.$toast.success('Excel exportado com sucesso!');
        },
        error => {
            this.exportingExcel = false;
            console.error('Erro ao exportar laudos Excel:', error);
            const status = error.response?.status;
            if (status === 404) {
                this.$toast && this.$toast.error('Nenhum laudo encontrado para esta empresa.');
            } else if (status === 401 || status === 403) {
                this.$toast && this.$toast.error('Sem permissão para exportar laudos desta empresa.');
            } else {
                this.$toast && this.$toast.error('Falha ao exportar Excel.');
            }
        },
        { responseType: 'arraybuffer', params: { pivoted } }
    );
},

        goToClassificar(item) {
            this.$router.push({
                name: 'classificar',
                params: { object_id: item?.Id },
                query:  { sampleId: item?.Nome || '' }
            });
        },
        hasQueryParameters() {
            return Object.keys(this.$route.query).length > 0;
        },
        handleResize() {
            this.windowWidth = window.innerWidth;
            this.$nextTick(() => { this.checkTableScroll(); });
        },
        checkTableScroll() {
            const wrapper = this.$el?.querySelector('.table-responsive-wrapper');
            if (wrapper) {
                const hasScroll = wrapper.scrollWidth > wrapper.clientWidth;
                if (hasScroll) {
                    wrapper.classList.add('has-scroll');
                } else {
                    wrapper.classList.remove('has-scroll');
                }
            }
        },
        resetFilters() {
            this.filters = {
                empresa: null,
                responsavel: null,
                locationState: null,
                locationCity: null,
                equipamento: null,
                dataInicio: null,
                dataFim: null
            };
            this.applyFilters();
        }
    },
    async mounted() {
        const pagina = sessionStorage.getItem('paginaSalva');
        this.currentPage = pagina ? Number(pagina) : 1;
        this.startLoadingProgress();
        document.addEventListener('click', this.closeDropdown);
        window.addEventListener('scroll', this.closeDropdown, true);
        window.addEventListener('resize', this.handleResize);
        this.handleResize();
        this.$nextTick(() => { this.checkTableScroll(); });
        if (this.isSuperAdmin && this.canExportExcel) {
            this.fetchExportEmpresas();
        }
        if (this.hasQueryParameters()) {
            this.toggleFilters();
        } else {
            this.fetchAnalises();
        }
    },
    beforeDestroy() {
        clearInterval(this.loadingProgressTimer);
        document.removeEventListener('click', this.closeDropdown);
        window.removeEventListener('scroll', this.closeDropdown, true);
        window.removeEventListener('resize', this.handleResize);
    }

};
</script>

<style scoped>
.table th:first-child, 
.table td:first-child {
    width: 50px;
    text-align: center;
}

.loading-progress-wrap {
    padding: 20px 0;
}

.loading-progress-label {
    font-size: 0.9rem;
    color: #666;
    margin-bottom: 10px;
}

::v-deep(.loading-progress-bar) {
    border-radius: 8px;
    overflow: hidden;
}

::v-deep(.loading-progress-bar .progress-bar) {
    background: linear-gradient(135deg, #FF8C42 0%, #FF7A29 100%);
    font-weight: 600;
    font-size: 0.85rem;
    transition: width 0.2s ease;
}

.dropdown-action-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.btn-actions-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    background: transparent;
    border: none;
    border-radius: 6px;
    color: #666;
    font-size: 1.1rem;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease;
    user-select: none;
}

.btn-actions-icon:hover,
.btn-actions-icon--open {
    background-color: #f0f0f0;
    color: #333;
}

.dropdown-floating-menu {
    position: fixed;
    z-index: 99999;
    background: #ffffff;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.14);
    overflow: hidden;
    animation: dropdownFadeIn 0.15s ease;
}

@keyframes dropdownFadeIn {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
}

.dropdown-floating-item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 16px;
    background: none;
    border: none;
    font-size: 0.875rem;
    color: #333;
    cursor: pointer;
    text-align: left;
    transition: background-color 0.15s;
    white-space: nowrap;
}

.dropdown-floating-item:not(:last-child) {
    border-bottom: 1px solid #f5f5f5;
}

.dropdown-floating-item:hover {
    background-color: #FFF5EF;
    color: #FF7A29;
}

.dropdown-floating-item i {
    color: #FF8C42;
    font-size: 0.9rem;
    width: 16px;
    text-align: center;
}

.analises-container {
    animation: fadeIn 0.3s ease-in;
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
}

@keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
    gap: 12px;
}

.page-header-left {
    display: flex;
    align-items: center;
    gap: 1.25rem;
}

.page-header-right {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
}

.export-company-select {
    min-width: 220px;
    max-width: 280px;
}

.btn-back {
    background: linear-gradient(135deg, #FF8C42 0%, #FF7A29 100%);
    border: none;
    color: white;
    width: 45px;
    height: 45px;
    min-width: 45px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(255, 140, 66, 0.3);
    flex-shrink: 0;
}

.btn-back:hover {
    background: linear-gradient(135deg, #FF7A29 0%, #FF6810 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 140, 66, 0.4);
}

.btn-back i { font-size: 1.2rem; }

.page-title {
    font-size: 2rem;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 0;
    word-break: break-word;
}

.box {
    padding: 24px;
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-radius: 20px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
}

.box:hover { box-shadow: 0 6px 30px rgba(0, 0, 0, 0.12); }

.filters {
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-top: 15px;
}

.filters-row {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 15px;
    width: 100%;
}

.filters-row > * { min-width: 0; }

.filters-date-range {
  grid-column: 1 / -1;
}

.filter-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
}

.table-responsive-wrapper {
    width: 100%;
    overflow-x: auto;
    overflow-y: visible;
    -webkit-overflow-scrolling: touch;
    margin: 0;
    padding: 0;
    position: relative;
}

.table-responsive-wrapper::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 30px;
    background: linear-gradient(to left, rgba(255, 255, 255, 0.9), transparent);
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.table-responsive-wrapper.has-scroll::after { opacity: 1; }

::v-deep(.full-width-card) {
    width: 100%;
    margin: 0;
    padding: 0;
    border: none;
    box-shadow: none;
    background: transparent;
}

::v-deep(.full-width-card .card-body) {
    padding: 0;
    overflow: visible;
}

.btn-orange {
    background: linear-gradient(135deg, #FF8C42 0%, #FF7A29 100%);
    border: none;
    color: white;
    font-weight: 600;
    padding: 10px 24px;
    border-radius: 8px;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(255, 140, 66, 0.3);
    white-space: nowrap;
}

.btn-orange:hover {
    background: linear-gradient(135deg, #FF7A29 0%, #FF6810 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 140, 66, 0.4);
    color: white;
}

.btn-orange:active  { transform: translateY(0); }
.btn-orange:disabled { background: #cccccc; cursor: not-allowed; box-shadow: none; }

.btn-orange-action {
    background-color: #FF8C42;
    border: none;
    color: white;
    font-weight: 600;
    padding: 8px 16px;
    border-radius: 6px;
    transition: all 0.3s ease;
    font-size: 0.9rem;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}

.btn-orange-action:hover {
    background-color: #FF7A29;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 140, 66, 0.35);
    color: white;
}

.btn-orange-action:active { transform: translateY(0); }

.btn-danger-action {
    background-color: #dc3545;
    border: none;
    color: white;
    font-weight: 600;
    padding: 8px 16px;
    border-radius: 6px;
    transition: all 0.3s ease;
    font-size: 0.9rem;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}

.btn-danger-action:hover {
    background-color: #c82333;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(220, 53, 69, 0.35);
}

.btn-danger-action:active { transform: translateY(0); }

.btn-cancel {
    background-color: white;
    border: 2px solid #FF8C42;
    color: #FF8C42;
    font-weight: 600;
    padding: 10px 24px;
    border-radius: 8px;
    transition: all 0.3s ease;
}

.btn-cancel:hover {
    background-color: #FFF5EF;
    border-color: #FF7A29;
    color: #FF7A29;
}

.message {
    margin: 0;
    text-align: center;
    font-size: 1.2rem;
    padding: 20px;
}

.error { color: #fb725f; font-weight: 600; }

::v-deep(.not-approved) { color: #fb725f; font-weight: 600; }

::v-deep(.actionsWrapper) {
    display: flex;
    flex-wrap: nowrap;
    gap: 8px;
    justify-content: center;
    min-width: 48px;
    position: sticky;
    right: 0;
    background: inherit;
    z-index: 2;
}

.action-buttons {
    display: flex;
    flex-wrap: nowrap;
    gap: 8px;
    justify-content: center;
    align-items: center;
}

.slide-enter-active,
.slide-leave-active { transition: all 0.2s ease-in-out; }

.slide-enter { transform: translateY(-10px); opacity: 0; }
.slide-leave-to { transform: translateY(-10px); opacity: 0; }

::v-deep(.page-link) {
    border-radius: 8px;
    color: #1C4E7A;
    margin-left: 8px;
}

::v-deep(.page-link):hover {
    color: #FFF;
    background-color: #FF8C42;
    border-color: #FF8C42;
}

::v-deep(.page-item.active .page-link) {
    background-color: #FF8C42;
    border-color: #FF8C42;
}

::v-deep(.page-item:first-child .page-link),
::v-deep(.page-item:last-child .page-link) { border-radius: 8px; }

.pdf-loading-overlay {
    position: fixed;
    top: 0; left: 0;
    width: 100vw; height: 100vh;
    background: rgba(255, 255, 255, 0.85);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.pdf-loading-overlay .spinner { width: 3rem; height: 3rem; }
.pdf-loading-overlay .text {
    margin-top: 12px;
    font-size: 1.1rem;
    color: #333;
    font-weight: 500;
}

.bulk-actions-bar {
    background: linear-gradient(135deg, #FF8C42 0%, #FF7A29 100%);
    border-radius: 12px;
    box-shadow: 0 4px 15px rgba(255, 140, 66, 0.3);
    position: sticky;
    top: 10px;
    z-index: 100;
}

.bulk-actions-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
}

.bulk-actions-buttons { display: flex; gap: 12px; flex-wrap: wrap; }

::v-deep(.modern-table) {
    border-radius: 12px;
    overflow: visible;
    width: 100%;
    min-width: 100%;
    table-layout: auto;
}

::v-deep(.cell-truncate) {
    max-width: 150px;
    min-width: 80px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

::v-deep(.cell-truncate:hover) {
    overflow: visible;
    white-space: normal;
    word-break: break-word;
    position: relative;
    z-index: 10;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    border-radius: 4px;
    padding: 8px 10px !important;
}

::v-deep(.modern-table thead) {
    background: linear-gradient(135deg, #FF8C42 0%, #FF7A29 100%);
    color: white;
}

::v-deep(.modern-table thead th) {
    font-weight: 700;
    text-transform: uppercase;
    font-size: 0.8rem;
    letter-spacing: 0.5px;
    padding: 14px 10px;
    border: none;
    white-space: pre-wrap;
    vertical-align: middle;
}

::v-deep(.modern-table thead th:last-child) {
    position: sticky;
    right: 0;
    background: linear-gradient(135deg, #FF8C42 0%, #FF7A29 100%);
    z-index: 3;
    box-shadow: -4px 0 8px rgba(0, 0, 0, 0.1);
}

::v-deep(.modern-table tbody tr) { transition: all 0.2s ease; }
::v-deep(.modern-table tbody tr:hover) { background-color: #FFF5EF; }

::v-deep(.modern-table tbody td) {
    padding: 12px 10px;
    vertical-align: middle;
    font-size: 0.9rem;
}

::v-deep(.modern-table tbody td:last-child) {
    position: sticky;
    right: 0;
    background: inherit;
    z-index: 2;
    box-shadow: -4px 0 8px rgba(0, 0, 0, 0.05);
}

::v-deep(.modern-table.table-striped tbody tr:nth-of-type(odd) td:last-child) {
    background-color: rgba(0, 0, 0, 0.05);
}

::v-deep(.modern-table.table-striped tbody tr:nth-of-type(even) td:last-child) {
    background-color: #fff;
}

::v-deep(.modern-table tbody tr:hover td:last-child) { background-color: #FFF5EF; }

.lineflex {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
}

.results-info {
    font-size: 0.9rem;
    color: #666;
    margin-top: 12px;
}

@media (min-width: 1600px) {
    ::v-deep(.modern-table thead th),
    ::v-deep(.modern-table tbody td) { padding: 14px 12px; }
    .btn-orange-action { padding: 10px 18px; font-size: 0.95rem; }
    ::v-deep(.cell-truncate) { max-width: 180px; min-width: 100px; }
}

@media (max-width: 1599px) and (min-width: 1400px) {
    ::v-deep(.modern-table thead th) { padding: 12px 8px; font-size: 0.75rem; }
    ::v-deep(.modern-table tbody td) { padding: 10px 8px; font-size: 0.85rem; }
    .btn-orange-action { padding: 7px 14px; font-size: 0.85rem; }
    ::v-deep(.cell-truncate) { max-width: 150px; min-width: 90px; }
}

@media (max-width: 1399px) and (min-width: 1200px) {
    .box { padding: 20px; }
    ::v-deep(.modern-table thead th) { padding: 10px 6px; font-size: 0.7rem; letter-spacing: 0.3px; }
    ::v-deep(.modern-table tbody td) { padding: 8px 6px; font-size: 0.8rem; }
    .btn-orange-action { padding: 6px 12px; font-size: 0.8rem; }
    ::v-deep(.cell-truncate) { max-width: 130px; min-width: 80px; }
}

@media (max-width: 1199px) {
    .box { padding: 18px; }
    ::v-deep(.modern-table thead th) { padding: 10px 5px; font-size: 0.68rem; letter-spacing: 0; }
    ::v-deep(.modern-table tbody td) { padding: 8px 5px; font-size: 0.78rem; }
    .btn-actions-icon { width: 30px; height: 30px; font-size: 1rem; }
    ::v-deep(.cell-truncate) { max-width: 120px; min-width: 70px; }
}

@media (max-width: 991px) {
    .page-title { font-size: 1.75rem; }
    .box { padding: 16px; border-radius: 16px; }
    .filters-row { grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); }
    .bulk-actions-content { flex-direction: column; text-align: center; }
    .bulk-actions-buttons { justify-content: center; width: 100%; }
    ::v-deep(.modern-table thead th) { padding: 8px 4px; font-size: 0.65rem; }
    ::v-deep(.modern-table tbody td) { padding: 6px 4px; font-size: 0.75rem; }
    .btn-orange-action, .btn-danger-action { padding: 5px 8px; font-size: 0.7rem; }
    ::v-deep(.actionsWrapper) { min-width: 44px; }
    ::v-deep(.cell-truncate) { max-width: 100px; min-width: 60px; }
}

@media (max-width: 768px) {
    .analises-container { padding: 0; }
    .page-header { margin-bottom: 1rem; }
    .page-title { font-size: 1.4rem; }
    .box { padding: 12px; border-radius: 12px; margin: 0 -8px; width: calc(100% + 16px); }
    .table-responsive-wrapper { margin: 0 -12px; padding: 0 12px; width: calc(100% + 24px); }
    .filters-row { grid-template-columns: 1fr; gap: 12px; }
    .filter-actions { flex-direction: column; width: 100%; }
    .filter-actions button { width: 100%; }
    .bulk-actions-bar { border-radius: 8px; margin: 0 -8px; width: calc(100% + 16px); }
    .bulk-actions-buttons { flex-direction: column; width: 100%; }
    .bulk-actions-buttons button { width: 100%; margin: 0; }
    ::v-deep(.modern-table thead th) { padding: 8px 4px; font-size: 0.6rem; }
    ::v-deep(.modern-table tbody td) { padding: 6px 4px; font-size: 0.7rem; }
    ::v-deep(.actionsWrapper) { min-width: 40px; }
    ::v-deep(.pagination) { flex-wrap: wrap; justify-content: center; gap: 4px; }
    ::v-deep(.page-link) { padding: 6px 10px; margin-left: 4px; font-size: 0.8rem; }
    ::v-deep(.cell-truncate) { max-width: 85px; min-width: 50px; }
}

@media (max-width: 576px) {
    .btn-back { width: 38px; height: 38px; min-width: 38px; border-radius: 10px; }
    .btn-back i { font-size: 1rem; }
    .page-title { font-size: 1.2rem; }
    .box { padding: 10px; border-radius: 10px; }
    .lineflex { flex-direction: column; align-items: stretch; }
    .lineflex .btn-orange { width: 100%; }
    .btn-orange { padding: 10px 16px; font-size: 0.9rem; }
    .btn-orange-action, .btn-danger-action { padding: 4px 6px; font-size: 0.6rem; }
    ::v-deep(.modern-table thead th) { padding: 6px 3px; font-size: 0.55rem; letter-spacing: 0; }
    ::v-deep(.modern-table tbody td) { padding: 5px 3px; font-size: 0.65rem; }
    .message { font-size: 1rem; padding: 16px 8px; }
    .results-info { font-size: 0.8rem; text-align: center; }
    ::v-deep(.page-link) { padding: 4px 8px; margin-left: 2px; font-size: 0.75rem; }
    ::v-deep(.actionsWrapper) { min-width: 36px; }
    ::v-deep(.cell-truncate) { max-width: 75px; min-width: 45px; }
    .btn-actions-icon { width: 28px; height: 28px; font-size: 0.95rem; }
}

@media (max-width: 400px) {
    .page-header-left { width: 100%; }
    .page-title { font-size: 1.1rem; }
    .box { padding: 8px; }
    ::v-deep(.modern-table thead th) { padding: 5px 2px; font-size: 0.5rem; }
    ::v-deep(.modern-table tbody td) { padding: 4px 2px; font-size: 0.6rem; }
    ::v-deep(.actionsWrapper) { min-width: 32px; }
    ::v-deep(.cell-truncate) { max-width: 60px; min-width: 40px; }
}

.period-modal-hint {
    margin: 0 0 12px;
    color: #6b7280;
    font-size: 0.9rem;
}

.period-modal-loading {
    display: flex;
    align-items: center;
    margin: 12px 0 0;
    color: #6b7280;
    font-size: 0.9rem;
}

.btn-cancel-selection {
    background-color: transparent;
    border: 2px solid white;
    color: white;
    font-weight: 600;
    padding: 6px 14px;
    border-radius: 6px;
    transition: all 0.3s ease;
    font-size: 0.9rem;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}

.btn-cancel-selection:hover {
    background-color: rgba(255, 255, 255, 0.15);
}

@media (max-width: 576px) {
    .page-header-right {
        width: 100%;
    }

    .page-header-right .btn-orange {
        width: 100%;
    }
}
</style>

